import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

// Components
import Navigation from "@companion-core/web/src/Navigation/Navigation";
import Header from "./Components/Header";
import Footer from "./Components/Footer";

// Styles
import "Assets/Styles/scss/global.scss";
import colors from "Assets/Styles/scss/_exports.module.scss";

// Document Title
import { ConfigProvider, Spin } from "antd";
import { usePopulateMember } from "@companion-core/shared/app/Hooks/member";
import { AccountPopover } from "@companion-core/web/src/Components/AccountPopover";
import { NewsletterModal } from "@companion-core/shared/app/Components/NewsletterModal/NewsletterModal";
import { TabBar } from "@companion-core/shared/app/Components/TabBar";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { CONFIG_TYPE } from "@companion-core/shared/app/Constants/config";
import { setInterceptors } from "@companion-core/shared/app/Utils/interceptors";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { onBeforeSSOLogin } from "@companion-core/shared/app/Utils/sso";
import { isRequestDetailDocumentTitleManaged, updateDocumentTitle } from "Utilities/title";
import { hasCodeQueryParam } from "Utilities/navigation";
import { antdComponentsTheme } from "Utilities/antdTheme";

import { loadLocale } from "@companion-core/shared/app/Utils/datetime";
import { useI18nStore } from "@companion-core/shared/app/Store/i18nStore";
import { AppDownloadBanner } from "@companion-core/web/src/Components/AppDownloadBanner";
import { isMobileDevice } from "@companion-core/shared/app/Utils/platform";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";

interface AppContentProps {
  showHeaderAndFooter?: boolean;
}

const ActivityIndicator = () => {
  return (
    <div className="app-loader">
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: colors.textPrimary,
            fontFamily: "Primary",
          },
          components: antdComponentsTheme,
        }}>
        <Spin data-testid="app__loader" size="large" />
      </ConfigProvider>
    </div>
  );
};

// Render the application content.
const AppContent = ({ showHeaderAndFooter = true }: AppContentProps) => {
  const { isIntroPopUpVisible } = useBellStore();
  const { authenticated, isAppDownloadBannerVisible, setIsAppDownloadBannerVisible } = useAuthStore(
    (state) => ({
      authenticated: state.authenticated,
      isAppDownloadBannerVisible: state.isAppDownloadBannerVisible,
      setIsAppDownloadBannerVisible: state.setIsAppDownloadBannerVisible,
    })
  );
  const { config } = useConfigStore((state) => ({ config: state.config }));

  const hasBellTooltip = useMemo(
    () => (config?.modules?.concierge?.enabled ? isIntroPopUpVisible : false),
    [config, isIntroPopUpVisible]
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: colors.textPrimary,
          fontFamily: "Primary",
        },
        components: antdComponentsTheme,
      }}>
      <div className="app-container">
        {showHeaderAndFooter && <Header />}
        <div className="page-content">
          <Navigation />
        </div>
        <AccountPopover />
        {config?.modules?.newsletter?.enabled && authenticated && <NewsletterModal />}
        {showHeaderAndFooter && (
          <>
            {authenticated && isAppDownloadBannerVisible && isMobileDevice() && !hasBellTooltip && (
              <AppDownloadBanner
                className="app-download-banner--tab-bar"
                onClose={() => setIsAppDownloadBannerVisible(false)}
              />
            )}
            <TabBar />
          </>
        )}
        {showHeaderAndFooter && <Footer />}
      </div>
    </ConfigProvider>
  );
};

// Trigger the usePopulateMember hooks and render AppContent when member is populated.
const AppContentWithMemberHook = ({ showHeaderAndFooter = true }: AppContentProps) => {
  const { isPopulatedMember } = usePopulateMember();

  return isPopulatedMember ? (
    <AppContent showHeaderAndFooter={showHeaderAndFooter} />
  ) : (
    <ActivityIndicator />
  );
};

// Represents the main application component.
const App = () => {
  const navigate = useNavigate();
  const { authenticated, loggingOut } = useAuthStore((state) => ({
    authenticated: state.authenticated,
    loggingOut: state.loggingOut,
  }));
  const [searchParams] = useSearchParams();
  const isSSOCompanionWithCodeParam = useMemo(
    () => hasCodeQueryParam(searchParams),
    [searchParams]
  );
  const pathname = useLocation().pathname;
  const pageSlug = pathname.split("/").length > 1 ? pathname.split("/")[1] : "";
  const isCompanionSSOOnly = getModule("sso")?.options?.ssoOnly;
  const { failure, interceptorLoaded, fetching } = useConfigStore();
  const isFailure = useMemo(
    () => !!failure && (failure[CONFIG_TYPE.CONFIG] || failure[CONFIG_TYPE.BRANDING]),
    [failure]
  );
  const showHeaderAndFooter = useMemo(
    () => !isFailure && (!isCompanionSSOOnly || (authenticated && !isSSOCompanionWithCodeParam)),
    [isSSOCompanionWithCodeParam, isCompanionSSOOnly, authenticated, failure]
  );
  const isMyAccountPage = useMemo(() => pageSlug === "my-account", [pageSlug]);
  const [isLoaded, setIsLoaded] = useState(false);
  const isFetching = useMemo(
    () => !!fetching && Object.keys(fetching).some((key) => fetching[key]),
    [fetching]
  );
  const queryClient = useQueryClient();
  const { locale: lang } = useI18nStore((state) => ({ locale: state.locale }));

  if (!isLoaded && !interceptorLoaded) {
    setIsLoaded(true);
    setInterceptors(navigate);
  }

  useEffect(() => {
    if (isSSOCompanionWithCodeParam && authenticated) {
      onBeforeSSOLogin();
    }
  }, []);

  useEffect(() => {
    if (!isRequestDetailDocumentTitleManaged(pathname)) {
      updateDocumentTitle(pathname);
    }
    if (isMyAccountPage) {
      if (!queryClient.isFetching({ queryKey: ["member"] })) {
        queryClient.invalidateQueries({ queryKey: ["member"] });
      }
    }
  }, [pathname]);

  useEffect(() => {
    if (lang) loadLocale(lang);
  }, [lang]);

  return isFetching || loggingOut ? (
    <ActivityIndicator />
  ) : !isSSOCompanionWithCodeParam ? (
    <AppContentWithMemberHook showHeaderAndFooter={showHeaderAndFooter} />
  ) : (
    <AppContent showHeaderAndFooter={showHeaderAndFooter} />
  );
};

export default App;
